import type { HomeyAPI, App } from 'homey-api';
import semver from 'semver';

import { HomeyAppVersionError, HomeySystemVersionError } from '../../lib/InternalError.js';

import type { AppProps } from '../../store/apps/AppsStore.ts';
import { BaseApiStore, type FetchArgs } from '../../store/BaseApiStore.ts';

interface Script {
  id: string;
  lastExecuted: string | null;
  name: string;
  version: number;
  code: string;
}

export type ScriptProps = {
  [K in keyof Script as Script[K] extends Function ? never : K]: Script[K];
};

type ScriptsById = { [key: string]: ScriptProps };

interface State {
  data: null;
  byId: ScriptsById | null;
  app: AppProps | null;
  appInstance: App | null;
  isLoading: boolean;
  error: unknown;
}

export type ScriptsStoreState = State;

export class ScriptsStore extends BaseApiStore<State, App> {
  constructor() {
    super('scripts');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      app: null,
      appInstance: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.apps, reference: this });

      if (abortController.signal.aborted) {
        return;
      }

      const appPromise = api.apps.getApp({ id: 'com.athom.homeyscript', $cache: cache });
      const [appInstance] = await Promise.all([appPromise]);

      const satisfiesHomeyVersion = semver.satisfies(api.homey.softwareVersion, '>=5.0.0', {
        includePrerelease: true,
      });

      if (satisfiesHomeyVersion === false) {
        throw new HomeySystemVersionError('invalid_homey_version');
      }

      const satisfiesAppVersion = semver.satisfies(appInstance.version, '>=3.3.0');

      if (satisfiesAppVersion === false) {
        throw new HomeyAppVersionError('invalid_app_version');
      }

      const scripts = (await appInstance.get({ path: 'script' })) as Promise<
        Record<
          string,
          {
            id: string;
            lastExecuted: string | null;
            name: string;
            version: number;
          }
        >
      >;

      const byId: ScriptsById = {};

      for (const [id, script] of Object.entries(scripts)) {
        byId[id] = {
          ...script,
          code: '',
        };
      }

      if (abortController.signal.aborted) {
        return;
      }

      this.set({
        ...this.createInitialState(),
        byId: byId,
        app: {
          ...appInstance,
        },
        appInstance: appInstance,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.apps, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.apps.on('app.create', this.handleCreate);
    api.apps.on('app.update', this.handleUpdate);
    api.apps.on('app.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.apps.off('app.create', this.handleCreate);
    api.apps.off('app.update', this.handleUpdate);
    api.apps.off('app.delete', this.handleDelete);
  }

  getInstanceFromCache(app: { id: string }) {
    return this.getCache()[app.id];
  }

  getCache(): { [key: string]: App } {
    const api = this.getApi();
    const cache = api.apps.__cache['app'];
    return cache;
  }

  handleCreate = (createdApp: App) => {
    this.DEBUG && console.info(`create:app:${createdApp.id}`);
    const appInstance = this.getInstanceFromCache(createdApp);

    if (appInstance == null || appInstance.id !== 'com.athom.homeyscript') {
      return;
    }

    const appProps = { ...appInstance };

    this.set({
      appInstance: appInstance,
      app: appProps,
    });
  };

  handleUpdate = (updatedApp: App) => {
    this.DEBUG && console.info(`update:app:${updatedApp.id}`);

    if (updatedApp.id !== 'com.athom.homeyscript') {
      return;
    }

    this.addToQueue(updatedApp.id, updatedApp);

    this.batch((updateQueue) => {
      const state = this.get();
      const queuedApp = updateQueue['com.athom.homeyscript'];
      const appInstance = this.getInstanceFromCache(queuedApp);

      if (appInstance == null) {
        this.DEBUG && console.info(`update:app:missing-data`);
        return;
      }

      if (appInstance == null || state.appInstance == null) {
        this.DEBUG && console.info(`update:app:unknown`);
        return;
      }

      const appProps = { ...appInstance };

      this.set({
        appInstance: appInstance,
        app: appProps,
      });
    });
  };

  handleDelete = (deletedApp: App) => {
    this.DEBUG && console.info(`delete:app:${deletedApp.id}`);

    if (deletedApp.id !== 'com.athom.homeyscript') {
      return;
    }

    this.set({
      appInstance: null,
      app: null,
      byId: null,
    });
  };

  restore() {
    const id = this.get().appInstance?.id;

    if (id == null) {
      return;
    }

    const appInstance = this.getInstanceFromCache({ id });
    const appProps = { ...appInstance };

    this.set({
      appInstance: appInstance,
      app: { ...appProps },
    });
  }

  async getScript({ scriptId }: { scriptId: string }) {
    const state = this.get();

    // Includes code and lastExecuted.
    const script = await state.appInstance?.get({
      path: `script/${encodeURIComponent(scriptId)}`,
    });

    const nextById = { ...state.byId };
    nextById[scriptId] = {
      ...nextById[scriptId],
      code: '',
      ...script,
    };

    this.set({
      byId: nextById,
    });

    return script;
  }

  async createScript({ script }: { script: { name: string; code: string } }) {
    const state = this.get();

    if (state.appInstance == null) {
      throw new Error('App instance is not available.');
    }

    const newScript = await state.appInstance.post({
      path: 'script',
      body: {
        name: script.name,
        code: script.code,
      },
    });

    this.set({
      byId: {
        ...state.byId,
        [newScript.id]: newScript,
      },
    });

    return newScript;
  }

  async updateScript({ id, script }: { id: string; script: Partial<ScriptProps> }) {
    const state = this.get();

    if (state.appInstance == null) {
      throw new Error('App instance is not available.');
    }

    await state.appInstance.put({
      path: `script/${encodeURIComponent(id)}`,
      body: {
        name: script.name,
        code: script.code,
      },
    });

    {
      const state = this.get();

      if (state.byId == null) {
        throw new Error('Scripts byId is not available.');
      }

      if (state.byId[id] == null) {
        throw new Error(`Script with id ${id} is not available.`);
      }

      const nextScript = {
        ...state.byId[id],
        ...script,
      };

      this.set({
        byId: {
          ...state.byId,
          [id]: nextScript,
        },
      });

      return nextScript;
    }
  }

  async deleteScript({ id }: { id: string }) {
    const state = this.get();

    if (state.appInstance == null) {
      throw new Error('App instance is not available.');
    }

    await state.appInstance.delete({ path: `script/${encodeURIComponent(id)}` });

    const nextById = { ...state.byId };
    delete nextById[id];

    this.set({
      byId: nextById,
    });
  }

  async testScript({ id, script }: { id: string; script?: { code: string } }) {
    const state = this.get();

    if (state.appInstance == null) {
      throw new Error('App instance is not available.');
    }

    const result = (await state.appInstance.post({
      path: `script/${encodeURIComponent(id)}/run`,
      body: {
        code: script != null ? script.code : undefined,
      },
    })) as Promise<unknown>;

    return result;
  }
}

export const scriptsStore = new ScriptsStore();
